// Month view — High-Contrast HUD aesthetic
// Desktop: glassmorphism grid with gradient borders
// Mobile: PulseTimeline condensed cards

import { useEffect, useMemo, useState } from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameMonth,
  isToday,
  parseISO,
  isPast,
  isFuture,
} from 'date-fns';

import { cn } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  closestCenter,
} from '@dnd-kit/core';

import {
  fetchCalendarMonth,
  normalizeCalendarMonth,
  buildMergedCalendarItemsForDay,
  type DayCalendarData,
} from '@/lib/calendar-month';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import {
  useUpdatePlannedSession,
} from '@/hooks/useCalendarMutations';
import { markDragOperationComplete } from '@/hooks/useAutoMatchSessions';
import { toast } from '@/hooks/use-toast';

import type { PlannedWorkout, CompletedActivity } from '@/types';
import type { CalendarSession } from '@/lib/api';
import type { CalendarItem } from '@/types/calendar';
import { normalizeSportType, mapIntensityToIntent } from '@/lib/session-utils';

import { DroppableDayCell } from '../DroppableDayCell';
import { CalendarWorkoutStack } from '../cards/CalendarWorkoutStack';
import { PulseTimeline } from './PulseTimeline';
import { SwipeIndicator } from '../SwipeIndicator';
import { capitalizeTitle } from '@/adapters/calendarAdapter';
import { sortCalendarItems } from '../cards/sortCalendarItems';
import { SessionCard } from '@/components/sessions/SessionCard';
import { Loader2 } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

const TEAL = '0, 255, 200';
const GREY_TEXT = '#A0A0A0';
const CARD_BG = 'rgba(12, 16, 24, 0.9)';
const BORDER_INACTIVE = 'rgba(255, 255, 255, 0.12)';

interface MonthViewProps {
  currentDate: Date;
  onActivityClick?: (
    planned: PlannedWorkout | null,
    completed: CompletedActivity | null,
    session?: CalendarSession | null
  ) => void;
  /** When set, open the session popup for this id once month data is loaded */
  sessionIdToOpen?: string | null;
  /** Called after session popup has been opened (clears sessionIdToOpen) */
  onSessionOpened?: () => void;
}

export function MonthView({
  currentDate,
  onActivityClick,
  sessionIdToOpen,
  onSessionOpened,
}: MonthViewProps) {
  const queryClient = useQueryClient();
  const updateSession = useUpdatePlannedSession();
  const isMobile = useIsMobile();

  const [activeId, setActiveId] = useState<string | null>(null);
  const [draggedSession, setDraggedSession] = useState<CalendarSession | null>(null);
  const [draggedItem, setDraggedItem] = useState<CalendarItem | null>(null);
  const [dragSourceDate, setDragSourceDate] = useState<string | null>(null);
  const [recentlyDropped, setRecentlyDropped] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const monthKey = format(monthStart, 'yyyy-MM');

  const { data: monthData, isLoading } = useAuthenticatedQuery({
    queryKey: ['calendar', 'month', monthKey],
    queryFn: () => fetchCalendarMonth(currentDate),
    retry: 1,
  });

  const dayDataMap = useMemo(() => {
    if (!monthData) return new Map<string, DayCalendarData>();
    const normalized = normalizeCalendarMonth(monthData);
    return new Map(normalized.map((d) => [d.date, d]));
  }, [monthData]);

  const days = useMemo(() => {
    const start = startOfWeek(monthStart, { weekStartsOn: 1 });
    const end = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [monthStart, monthEnd]);

  const activityIdBySessionId = useMemo(() => {
    if (!monthData) return {};
    const map: Record<string, string> = {};
    for (const session of [...monthData.planned_sessions, ...monthData.workouts]) {
      if (session.completed_activity_id) {
        map[session.id] = session.completed_activity_id;
      }
    }
    for (const activity of monthData.completed_activities || []) {
      if (activity.planned_session_id) {
        map[activity.planned_session_id] = activity.id;
      }
    }
    return map;
  }, [monthData]);

  // Open session popup when navigated from Today "Expand" (calendarOpenSessionId)
  useEffect(() => {
    if (!sessionIdToOpen || !monthData || !onActivityClick) return;
    const session = [...monthData.planned_sessions, ...monthData.workouts].find(
      (s) => s.id === sessionIdToOpen
    );
    if (!session) return;
    const activity = monthData.completed_activities?.find(
      (a) => a.id === sessionIdToOpen || a.planned_session_id === sessionIdToOpen
    ) ?? null;
    onActivityClick(
      {
        id: session.id,
        date: session.date || '',
        sport: normalizeSportType(session.type),
        intent: mapIntensityToIntent(session.intensity),
        title: capitalizeTitle(session.title || ''),
        description: session.notes || '',
        duration: session.duration_minutes || 0,
        completed: session.status === 'completed',
      },
      activity,
      session
    );
    onSessionOpened?.();
  }, [sessionIdToOpen, monthData, onActivityClick, onSessionOpened]);

  const getCalendarItemsForDay = (date: Date): CalendarItem[] => {
    if (!monthData) return [];
    const dayKey = format(date, 'yyyy-MM-dd');
    const dayData = dayDataMap.get(dayKey);
    if (!dayData) return [];
    return buildMergedCalendarItemsForDay(dayData, monthData.completed_activities);
  };

  // Drag handlers (same logic as before)
  const handleDragStart = (event: DragStartEvent) => {
    const session = event.active.data.current?.session as CalendarSession | undefined;
    const item = event.active.data.current?.item as CalendarItem | undefined;
    const mutableSessionId = session?.planned_session_id || session?.id;
    if (!mutableSessionId) return;
    setActiveId(event.active.id as string);
    setDraggedSession(session);
    setDraggedItem(item || null);
    if (session?.date) setDragSourceDate(format(parseISO(session.date), 'yyyy-MM-dd'));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setDraggedSession(null);
    setDraggedItem(null);
    setDragSourceDate(null);
    if (!over) return;
    const session = active.data.current?.session as CalendarSession | undefined;
    const targetDate = over.data.current?.date as string | undefined;
    if (!session || !targetDate) return;
    const oldDate = session.date ? format(parseISO(session.date), 'yyyy-MM-dd') : '';
    if (oldDate === targetDate) return;
    const mutableSessionId = session.planned_session_id || session.id;
    if (!mutableSessionId) {
      toast({ title: 'Move failed', description: 'Missing planned session ID.', variant: 'destructive' });
      return;
    }
    setRecentlyDropped(session.id);
    setTimeout(() => setRecentlyDropped(null), 600);
    const mutateOpts = {
      onSuccess: () => {
        markDragOperationComplete();
        queryClient.invalidateQueries({ queryKey: ['calendar'], exact: false });
        toast({ title: 'Session moved', description: `Moved to ${format(parseISO(targetDate), 'EEE, MMM d, yyyy')}` });
      },
    };
    updateSession.mutate({ sessionId: mutableSessionId, scheduledDate: targetDate }, mutateOpts);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" style={{ background: CARD_BG, borderRadius: '12px', border: `1px solid ${BORDER_INACTIVE}` }}>
        <Loader2 className="h-5 w-5 animate-spin mr-2" style={{ color: GREY_TEXT }} />
        <span style={{ color: GREY_TEXT }} className="font-mono text-sm">Loading calendar...</span>
      </div>
    );
  }

  // Mobile: PulseTimeline with all month days
  if (isMobile) {
    const currentMonthDays = days.filter(d => isSameMonth(d, currentDate));

    const handleMobileItemClick = (item: CalendarItem) => {
      if (!monthData) return;
      const session = [...monthData.planned_sessions, ...monthData.workouts].find((s) => s.id === item.id) ?? null;
      const activity = monthData.completed_activities.find(
        (a) => a.planned_session_id === item.id || (session?.workout_id && a.workout_id === session.workout_id)
      ) ?? null;
      onActivityClick?.(null, activity, session ?? undefined);
    };

    return (
      <div className="overflow-hidden h-full flex flex-col">
        <SwipeIndicator label="month" className="flex-shrink-0" />
        <PulseTimeline
          days={currentMonthDays}
          getItemsForDay={getCalendarItemsForDay}
          onItemClick={handleMobileItemClick}
          className="flex-1 px-1"
        />
      </div>
    );
  }

  // Desktop: HUD grid
  const weekDays = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

  return (
    <div className="overflow-hidden h-full flex flex-col">
      {/* Header row */}
      <div
        className="grid grid-cols-7 flex-shrink-0 rounded-t-xl"
        style={{ background: 'rgba(12,16,24,0.7)', borderBottom: `1px solid ${BORDER_INACTIVE}` }}
      >
        {weekDays.map((d) => (
          <div key={d} className="py-2 text-center font-mono text-[10px] uppercase tracking-wider" style={{ color: GREY_TEXT }}>
            {d}
          </div>
        ))}
      </div>

      <DndContext collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-7 flex-1 min-h-0" style={{ gridTemplateRows: 'repeat(6, minmax(90px, 1fr))' }}>
          {days.map((day, idx) => {
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isCurrentDay = isToday(day);
            const items = getCalendarItemsForDay(day);
            const totalLoad = items.reduce((s, i) => s + (i.load || 0), 0);
            const maxLoad = 150; // Rough max for micro-bar scaling

            return (
              <DroppableDayCell
                key={idx}
                date={day}
                className="relative flex flex-col min-h-0 overflow-hidden transition-colors duration-150"
                style={{
                  background: isCurrentDay ? 'rgba(14,22,32,0.85)' : CARD_BG,
                  borderRight: idx % 7 < 6 ? `1px solid rgba(255,255,255,0.04)` : 'none',
                  borderBottom: `1px solid rgba(255,255,255,0.04)`,
                  boxShadow: isCurrentDay
                    ? `inset 0 0 30px rgba(${TEAL}, 0.03), 0 0 20px rgba(${TEAL}, 0.06)`
                    : 'none',
                  outline: isCurrentDay ? `1px solid rgba(${TEAL}, 0.4)` : 'none',
                }}
              >
                {/* Day number + micro load bar */}
                <div className="px-1.5 pt-1 pb-0.5 flex-shrink-0 flex items-center justify-between">
                  <span
                    className={cn(
                      'font-mono text-xs tabular-nums font-medium',
                      !isCurrentMonth && 'opacity-30',
                    )}
                    style={{
                      color: isCurrentDay ? `rgba(${TEAL}, 0.95)` : isCurrentMonth ? '#c0c0c0' : GREY_TEXT,
                    }}
                  >
                    {format(day, 'd')}
                  </span>
                  {/* Micro load bar */}
                  {totalLoad > 0 && isCurrentMonth && (
                    <div className="w-8 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${Math.min((totalLoad / maxLoad) * 100, 100)}%`,
                          background: totalLoad > 100
                            ? `rgba(${TEAL}, 0.7)`
                            : totalLoad > 50
                              ? `rgba(${TEAL}, 0.4)`
                              : 'rgba(255,180,60,0.3)',
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Workout cards */}
                <div className="flex-1 flex flex-col gap-px px-0.5 pb-0.5 min-h-0 overflow-y-auto overflow-x-hidden">
                  {items.length > 0 && (() => {
                    const sortedItems = sortCalendarItems(items);
                    const visibleItems = sortedItems.slice(0, 3);
                    const remainingCount = sortedItems.length - 3;
                    const dayStr = format(day, 'yyyy-MM-dd');

                    return (
                      <>
                        {visibleItems.map((item) => {
                          const isDragging = activeId === item.id;
                          const isGhostSource = dragSourceDate === dayStr && isDragging;
                          const isDropped = recentlyDropped === item.id;
                          return (
                            <div key={item.id} className={cn('flex-shrink-0 relative', isDragging && 'opacity-30')}>
                              {isGhostSource && (
                                <div className="absolute inset-0 rounded border-2 border-dashed border-primary/40 bg-primary/5 z-10" />
                              )}
                              <div className={cn('transition-all duration-300', isDropped && 'animate-scale-in ring-2 ring-primary/50 shadow-lg')}>
                                <CalendarWorkoutStack
                                  items={[item]}
                                  variant="month"
                                  maxVisible={1}
                                  activityIdBySessionId={activityIdBySessionId}
                                  useNewCard
                                  isMobile={isMobile}
                                  onClick={(clickedItem) => {
                                    if (!monthData) return;
                                    const session = [...monthData.planned_sessions, ...monthData.workouts].find((s) => s.id === clickedItem.id) ?? null;
                                    const activity = monthData.completed_activities.find(
                                      (a) => a.planned_session_id === clickedItem.id || (session?.workout_id && a.workout_id === session.workout_id)
                                    ) ?? null;
                                    onActivityClick?.(null, activity, session ?? undefined);
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })}
                        {remainingCount > 0 && (
                          <div className="flex-shrink-0 font-mono text-[9px] text-center py-0.5" style={{ color: GREY_TEXT }}>
                            +{remainingCount} more
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </DroppableDayCell>
            );
          })}
        </div>

        <DragOverlay>
          {activeId && draggedItem && (
            <div className="opacity-90 shadow-xl rounded-lg ring-2 ring-primary animate-scale-in">
              <SessionCard session={draggedItem} density="compact" className="w-[160px]" />
            </div>
          )}
          {activeId && draggedSession && !draggedItem && (
            <div className="px-3 py-1.5 text-sm rounded-md shadow-lg font-mono" style={{ background: CARD_BG, border: `1px solid ${BORDER_INACTIVE}`, color: GREY_TEXT }}>
              {capitalizeTitle(draggedSession.title || 'Workout')}
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
